/**
 * The tasks cache (`KLQ-20260821-001` §B, §C, §E) — the first screen in this repo whose fetch
 * shape is a decision rather than an accident.
 *
 * FIVE RULES, EACH WITH A REASON THAT WOULD COST SOMETHING TO IGNORE
 *
 * 1. **Every list is a cursor, never an offset.** `useInfiniteQuery` over the server's keyset,
 *    with the whole filter object in the key. A page the server filtered is the only page that
 *    can be complete.
 * 2. **Every page is kept, and the DOM is bounded by a window instead** (`KLQ-20260910-001`
 *    §F). This was `maxPages: 5` — at the API's `DEFAULT_LIMIT = 50`, at most 250 rows in the
 *    DOM, a cap standing in for virtualisation. It bought that at a price the register had to
 *    print on screen: `maxPages` is a *rolling* window, so paging past it dropped the oldest
 *    page, and the keyset has no backwards cursor to fetch that page again — scroll far
 *    enough and task #1 was gone. `lib/windowing.ts` has done the real job since
 *    `KLQ-20260820-005` (`useAnchoredWindow`, spacer rows, no dependency), so the cap was
 *    paying for a service it no longer provided.
 *
 *    **What the cap also bought, and what now bounds it instead.** TanStack re-runs *every*
 *    cached page of an infinite query sequentially on a refetch, which is the biggest
 *    self-inflicted load trap in the default configuration (research 19 §11). With no cap
 *    that fan-out is bounded by how far a person actually scrolled rather than by a constant —
 *    six pages for somebody 300 rows deep, one for everybody else — and rule 5 is what keeps
 *    it from happening on views nobody is looking at. Rule 5 is load-bearing now, not merely
 *    tidy.
 * 3. **No `staleTime` here at all.** The client-wide default is 30 s (`app/providers.tsx`) and
 *    §B says this screen does not override it downward. An override *upward* would outlive the
 *    poll that exists to notice changes, so the absence of the line is the decision.
 * 4. **One poll per project.** Not one per list, and not four when the board is mounted. It is
 *    one query key, so every observer shares one interval and one request — and `TasksPage` is
 *    the only caller, so the rule survives a component that forgets it.
 * 5. **Invalidation is scoped to what is on screen.** A changed version refetches the mounted
 *    view and marks the other *lists* stale with `refetchType: 'none'` — the label registry is
 *    reference data and the signal says nothing about it. Without that rule one change on
 *    the board is 4 columns x 5 pages = 20 requests, which is rule 2's load trap arriving
 *    through the back door.
 */

import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  keepPreviousData,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  hierarchyQueryOptions,
  invalidateDrawings,
  uploadFile,
} from "@/features/drawings";
import type { UploadProgress } from "@/features/drawings";
import { membersQueryOptions } from "@/features/people";
import { PROJECTS_QUERY_KEY } from "@/features/projects";
import { SESSION_QUERY_KEY, useActiveWorkspaceId } from "@/features/session";
import { workspaceQueryKey } from "@/features/setup";
import type { Translate } from "@/lib/i18n";

import {
  addTaskAttachment,
  addTaskComment,
  bulkEditTasks,
  changeTaskStatus,
  closeTaskByVersion,
  createLabel,
  createTask,
  exportTasksCsv,
  fetchTaskCounts,
  generateTasks,
  listBranchTasks,
  listLabels,
  listSlotTasks,
  listTaskAttachments,
  listTaskComments,
  listTaskDeliverables,
  listTasks,
  listTaskWatchers,
  listMyTasks,
  readTask,
  readTaskAudience,
  readTasksVersion,
  reopenTask,
  setTaskWatching,
  updateTask,
} from "./api";
import {
  createTaskType,
  deleteTaskType,
  listTaskTypes,
  updateTaskType,
} from "./api";
import type {
  Task,
  TaskBulkEdit,
  TaskCreate,
  TaskGenerate,
  TaskStatus,
  TaskTypeCreate,
  TaskTypeUpdate,
  TaskUpdate,
  WorkspaceTaskType,
} from "./api";
import type { MyTaskFilters, TaskFilters, TaskView } from "./filters";
import { isStale } from "./problems";

/** How often the change signal is asked for. §E: 1.7 req/s per 50-user workspace, and no socket. */
export const TASKS_POLL_MS = 30_000;

/**
 * The label registry's own lifetime. Reference data — a project's vocabulary changes when
 * somebody edits it on the settings screen, which is not while a register is open — so it is
 * fetched once per project and shared by the filter bar, the rows and the create form (§B).
 */
export const LABELS_STALE_MS = 5 * 60_000;

/** Everything this screen caches about one project hangs off the project's own prefix. */
function tasksKey(projectId: string) {
  return [...PROJECTS_QUERY_KEY, "tasks", projectId] as const;
}

/**
 * Every list of this project's tasks, and nothing else under the project's prefix.
 *
 * Rule 5's sweep stops here rather than at `tasksKey`: the version signal is
 * `max(updated_at)` over the `task` table alone, so a task moving says nothing about the
 * project's label vocabulary. Sweeping the whole prefix would mark `'labels'` stale on every
 * task change and throw away a registry that is deliberately kept for `LABELS_STALE_MS`.
 */
function listsKey(projectId: string) {
  return [...tasksKey(projectId), "list"] as const;
}

/**
 * The **fetch shape** a view has — which is not always the view itself.
 *
 * The board earns its own prefix: its four columns pin `status` per column, so they are four
 * genuinely different requests, and rule 5 needs to name *what is on screen* to invalidate the
 * mounted one and no more. The plan view (`hierarchy`) earns nothing of the sort — it issues
 * `listTasks(projectId, filters, cursor)` with the very same filter object the register does
 * and groups the answer on the client. A key of its own would mean every register<->plan
 * toggle re-requests pages the cache already holds, which is the duplicate-call pattern
 * `~/.claude/CLAUDE.md` names. Two byte-identical requests are one query.
 *
 * Invalidation still names what is mounted, because it asks this function which cache entry
 * the mounted view is looking at rather than assuming a view is a key.
 */
type ListShape = "register" | "board";

function shapeOf(view: TaskView): ListShape {
  return view === "board" ? "board" : "register";
}

/** The prefix one fetch shape's lists share. */
function listKey(projectId: string, view: TaskView) {
  return [...listsKey(projectId), shapeOf(view)] as const;
}

/**
 * One list: the register, or one column of the board.
 *
 * The **whole filter object** is the last segment of the key — not a hand-rolled digest of it.
 * TanStack hashes keys with sorted object keys, so a total object (`filters.ts` keeps every
 * field present) hashes stably, and a filter added to the object is in the key without anybody
 * remembering to put it there.
 *
 * A board column passes `{ ...filters, status: ['open'] }`: the same object, narrowed, so a
 * column inherits every filter the bar sets and adds exactly one.
 */
export function taskListQueryOptions(
  projectId: string,
  filters: TaskFilters,
  view: TaskView,
) {
  return infiniteQueryOptions({
    queryKey: [...listKey(projectId, view), filters] as const,
    queryFn: ({ pageParam }) => listTasks(projectId, filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? null,
    // No `maxPages`: see rule 2. A rolling window over a cursor that cannot rewind is a list
    // that silently loses its own beginning.
  });
}

/**
 * The root `/tasks` page's list (`MCK-20260521-009`): the caller's own work, every project.
 *
 * **Under the session's prefix, not a project's.** The rows span projects, so no project's
 * rule-5 sweep may claim them — and the session prefix is what sign-out clears, which is exactly
 * the lifetime of *"what do I owe"*. Rules 1–3 hold as they do for the register: a cursor, every
 * page kept, the client-wide `staleTime`.
 *
 * **No poll.** The register's is driven by a per-project version signal
 * (`GET /task-version`), and there is no cross-project one; polling the list itself would re-run
 * every loaded page every interval for a page people open, read and leave. The list refetches
 * on mount and window focus under the client default, which is when the question is asked.
 */
export function myTaskListQueryOptions(filters: MyTaskFilters) {
  return infiniteQueryOptions({
    queryKey: [...SESSION_QUERY_KEY, "my-tasks", filters] as const,
    queryFn: ({ pageParam }) => listMyTasks(filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? null,
  });
}

export function useMyTaskList(filters: MyTaskFilters) {
  return useInfiniteQuery(myTaskListQueryOptions(filters));
}

export function useTaskList(
  projectId: string,
  filters: TaskFilters,
  view: TaskView,
  /**
   * Whether to ask at all. The plan fetches its rows a branch at a time and only wants the
   * whole register when a pivot needs to see rows no open level holds — so it passes `false`
   * the rest of the time, and the query stays a cache entry nobody is filling.
   *
   * Default `true`: every other caller *is* the register.
   */
  enabled = true,
) {
  return useInfiniteQuery({ ...taskListQueryOptions(projectId, filters, view), enabled });
}

/**
 * The tasks of the levels somebody has opened — **contents per branch** (`KLQ-20260825-008`
 * §5.1, owner 2026-09-10: *"if they are in a hierarchy then fetch when the node is opened"*).
 *
 * One cached query per open level, keyed by the level, the filters and how many pages of it
 * have been asked for. Closing a branch and reopening it costs nothing; opening a second one
 * costs one request, not a refetch of the first. The tree's *shape* is fetched whole and
 * separately — that is the half of §5.1 that has always worked.
 *
 * **A branch pages like any other list, and the sentinel is a row inside it** (owner,
 * 2026-09-10). `reach(nodeId)` is what that row calls; it raises the branch's page count, which
 * changes its key. `placeholderData` keeps the rows that are already there on screen while the
 * longer walk is in flight, so a branch grows rather than blinking.
 *
 * `useQueries` rather than a hook per level, because how many levels are open is not knowable
 * at compile time and hooks may not run in a loop. That is also why the cursor walk lives in
 * `listBranchTasks` rather than in an infinite query — see its note.
 */
export interface BranchTasks {
  readonly tasks: readonly Task[];
  readonly loading: boolean;
  /** Levels with more work than has been fetched — the ones that draw a sentinel row. */
  readonly more: ReadonlySet<string>;
  /** Ask one level for another page. Stable, so it may sit in an effect's dependencies. */
  readonly reach: (nodeId: string) => void;
}

export function useBranchTasks(
  projectId: string,
  filters: TaskFilters,
  levels: readonly string[],
): BranchTasks {
  // How many pages each open level has been asked for. Absent is one — the map holds only the
  // branches somebody has scrolled through, which is almost none of them.
  const [pages, setPages] = useState<Readonly<Record<string, number>>>({});

  const results = useQueries({
    queries: levels.map((nodeId) => {
      const wanted = pages[nodeId] ?? 1;
      return {
        queryKey: [...tasksKey(projectId), "branch", nodeId, filters, wanted] as const,
        queryFn: () => listBranchTasks(projectId, filters, nodeId, wanted),
        // The previous walk stays on screen while the longer one is in flight. Without it a
        // branch empties and refills, which reads as a bug on the row somebody is looking at.
        placeholderData: keepPreviousData,
      };
    }),
  });

  const reach = useCallback((nodeId: string) => {
    setPages((current) => ({ ...current, [nodeId]: (current[nodeId] ?? 1) + 1 }));
  }, []);

  // A map rather than a concatenation: a task pinned to a drawing is reported on the level that
  // holds the drawing, so two open levels can name the same task and the plan must see it once.
  const byId = new Map<string, Task>();
  const more = new Set<string>();
  let loading = false;
  results.forEach((page, index) => {
    if (page.isPending) loading = true;
    const nodeId = levels[index];
    if (nodeId !== undefined && page.data?.has_more === true) more.add(nodeId);
    for (const task of page.data?.items ?? []) byId.set(task.id, task);
  });

  return { tasks: [...byId.values()], loading, more, reach };
}

/**
 * The plan's per-level counts (`KLQ-20260825-008` §5.1).
 *
 * Keyed by the **filters**, like the list beside it, because it answers the same question: a
 * filter change invalidates both together and they can never be one refresh apart. It is not
 * keyed by the view, because a count does not depend on how the rows are arranged.
 *
 * One request per filter change, and no polling — a count is not live for the same reason the
 * register is not.
 */
export function taskCountsQueryOptions(
  projectId: string,
  filters: TaskFilters,
  under: readonly string[] = [],
) {
  return queryOptions({
    queryKey: [...tasksKey(projectId), "counts", filters, under] as const,
    queryFn: () => fetchTaskCounts(projectId, filters, under),
  });
}

export function useTaskCounts(
  projectId: string,
  filters: TaskFilters,
  under: readonly string[] = [],
) {
  return useQuery(taskCountsQueryOptions(projectId, filters, under));
}

/** The project's vocabulary, once per project. Never a lookup per row. */
export function projectLabelsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...tasksKey(projectId), "labels"] as const,
    queryFn: () => listLabels(projectId),
    staleTime: LABELS_STALE_MS,
  });
}

export function useProjectLabels(projectId: string) {
  return useQuery(projectLabelsQueryOptions(projectId));
}

/**
 * The studio's task types — one fetch per session, shared by three surfaces
 * (`KLQ-20260825-005`).
 *
 * **Under the workspace's key, not a project's**, because that is whose vocabulary it is: the
 * generate drawer reads it on every project, the register renders an icon from it on every
 * row, and studio settings edits it. One key means one request and one truth; a per-project
 * copy would be N requests for a list that cannot differ between them.
 *
 * The staleness is the label registry's, for the label registry's reason: this changes when
 * somebody edits it on the settings screen, which is not while a register is open. The tasks
 * version poll deliberately never sweeps it — `max(updated_at)` over `task` says nothing about
 * what a studio calls its work (rule 5).
 */
export function taskTypesQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: [...workspaceQueryKey(workspaceId), "task-types"] as const,
    queryFn: () => listTaskTypes(),
    staleTime: LABELS_STALE_MS,
  });
}

export function useTaskTypes(enabled = true) {
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    ...taskTypesQueryOptions(workspaceId ?? ""),
    enabled: enabled && workspaceId !== null,
  });
}

/**
 * The types a picker may offer: everything that is not archived, in the studio's own order.
 *
 * **The filter lives here and nowhere else.** Archiving means *"leaves the picker, stays on
 * the tasks"* — two halves of one rule — so the list arrives whole and exactly one selector
 * drops the retired rows. A second copy of this `filter` in a component is how the two halves
 * start disagreeing.
 */
export function offerableTypes(
  types: readonly WorkspaceTaskType[] | undefined,
): readonly WorkspaceTaskType[] {
  return (types ?? []).filter((type) => type.archived_at === null);
}

function useTaskTypesInvalidation() {
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceId() ?? "";
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: taskTypesQueryOptions(workspaceId).queryKey,
    });
  };
}

export function useCreateTaskType() {
  const invalidate = useTaskTypesInvalidation();
  return useMutation({
    mutationFn: (body: TaskTypeCreate) => createTaskType(body),
    onSuccess: invalidate,
  });
}

/**
 * Rename, recolour, reorder, archive — one write, and **no task list is invalidated**.
 *
 * That is not an omission, it is the design (`KLQ-20260825-005` §1): a task holds the type's
 * id and the client resolves it against this list, so refreshing this one entry re-renders
 * every task on screen. Sweeping the registers would spend a request per mounted list to learn
 * nothing that changed.
 */
export function useUpdateTaskType() {
  const invalidate = useTaskTypesInvalidation();
  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: TaskTypeUpdate }) =>
      updateTaskType(id, changes),
    onSuccess: invalidate,
  });
}

/**
 * Retire a type, whichever way the server decides.
 *
 * The list is re-read rather than patched in place, because the outcome is not knowable here:
 * `204` means *deleted* for a type nothing named and *archived* for one a task does, and a
 * client that assumed the first would drop a row the studio can still see.
 */
export function useDeleteTaskType() {
  const invalidate = useTaskTypesInvalidation();
  return useMutation({
    mutationFn: (id: string) => deleteTaskType(id),
    onSuccess: invalidate,
  });
}

/**
 * One word added to the vocabulary, from the filter bar.
 *
 * Only the registry is invalidated. A new label carries no task yet, so no list on this screen
 * can be wrong because of it — sweeping the lists here would cost the register a refetch to
 * learn nothing, which is rule 5 read backwards.
 */
export function useCreateLabel(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createLabel(projectId, name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: projectLabelsQueryOptions(projectId).queryKey,
      });
    },
  });
}

/**
 * A generation — the preview and the write, through one mutation (`KLQ-20260825-001`).
 *
 * `retry: false` for the same reason `useCreateTask` gives, and one the dry run sharpens: a
 * replayed *write* whose answer was lost would not double the tasks (the server skips a node
 * that already carries this template's task) but would double the numbers it burned. The
 * person retries with their eyes open.
 *
 * **A dry run invalidates nothing** — it wrote nothing, and sweeping the lists after a
 * preview would cost the register a refetch to learn what it already knows. A write
 * invalidates every cached task list, and the *drawings* too when it claimed deliverables:
 * `owning_task_id` is a column on the slot row, so the planliste and the tree are wrong the
 * moment a claim lands and nothing else would tell them.
 */
export function useGenerateTasks(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TaskGenerate) => generateTasks(projectId, body),
    retry: false,
    onSuccess: async (plan) => {
      if (!plan.created) return;
      await queryClient.invalidateQueries({ queryKey: listsKey(projectId) });
      if (plan.claimed_slots.length > 0)
        invalidateDrawings(queryClient, projectId);
    },
  });
}

/**
 * A task raised from the drawer (the register's entrance, or the node-narrowed register's).
 *
 * `retry: false` because the endpoint takes no `Idempotency-Key`: an automatic replay of a
 * request whose answer was lost would raise the task twice, each with its own permanent
 * number. The person retries by pressing the button again, eyes open.
 *
 * On success, rule 5 in its plainest form: one invalidation over every cached list, with the
 * default `refetchType` — the mounted list (the register the drawer sits over) refetches and
 * shows the new row, every unmounted filter combination merely goes stale. Awaited, so the
 * drawer closes onto a register that already asked the server rather than one about to.
 */
export function useCreateTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TaskCreate) => createTask(projectId, body),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: listsKey(projectId) });
    },
  });
}

/**
 * One intent across a selection (`KLQ-20260825-006` §E), and the write behind every in-grid
 * edit (`KLQ-20260828-001` §E).
 *
 * **The whole list is invalidated, not patched.** The answer says which rows were applied and
 * which were skipped, and the skipped ones still hold whatever the server holds — reproducing
 * that here would be a second opinion about state the server just gave us. One refetch of the
 * lists is also what the version digest would have caused within thirty seconds anyway; doing
 * it now only makes the screen agree with the write immediately.
 *
 * `retry: false`: a replay whose answer was lost would re-apply a delta that has already been
 * applied. `assignees.add` is idempotent, `status` is not — a second attempt at
 * `done → closed` answers `invalid_transition` and would read as a failure of the edit.
 */
export function useBulkEditTasks(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TaskBulkEdit) => bulkEditTasks(projectId, body),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: listsKey(projectId) });
    },
  });
}

/**
 * *"Rows arrived from somewhere that is not a mutation"* — the paste-import's refetch
 * (`KLQ-20260824-002`).
 *
 * The import adapter calls the generation and bulk-edit endpoints directly rather than through
 * the two mutations above, because it issues a *series* of requests whose per-row outcomes it
 * has to reconcile itself; a mutation per bucket would sweep the lists once per bucket and
 * refetch the register five times for one act. So the sweep happens once, here, when the whole
 * import has finished — and it is the same sweep those mutations do, from the same key.
 */
export function useTasksInvalidation(projectId: string): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: listsKey(projectId) });
    // An imported task can claim nothing, but it *is* a possible owner of a drawing the moment
    // it exists, and the planliste prints owners. One extra sweep on an act that already
    // refetched the register is not the traffic worth saving.
    invalidateDrawings(queryClient, projectId);
  }, [queryClient, projectId]);
}

/**
 * What one drop asks for. `from` and `filters` are not sent anywhere — they are what lets the
 * success path invalidate exactly the two columns the card left and entered, and nothing else.
 */
export interface TaskMove {
  readonly taskId: string;
  readonly from: TaskStatus;
  readonly to: TaskStatus;
  readonly filters: TaskFilters;
}

/**
 * A card, dropped (§C).
 *
 * No `onMutate` cache surgery: the bounded optimism the spec asks for — the card moves
 * immediately and moves back on failure — is the board's own render state, because rewriting
 * an infinite query's pages here would also rewrite the loaded counts §D derives from them,
 * turning a presentation courtesy into a wrong number. The cache changes only on the server's
 * answer.
 *
 * On success, rule 5 again, scoped tighter than the poll's version: every cached list is
 * marked stale without refetching, then the two columns the move actually touched refetch for
 * real. Awaited, so the board holds its optimistic placement until the cache agrees and the
 * card cannot flicker back through its origin column on the way.
 *
 * `retry: false` because a `409` here is the workflow answering, not a hiccup — an illegal
 * jump refused once is refused twice, and the verification gate does not open for persistence.
 */
export function useMoveTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (move: TaskMove) => changeTaskStatus(move.taskId, move.to),
    retry: false,
    onSuccess: async (_task, move) => {
      void queryClient.invalidateQueries({
        queryKey: listsKey(projectId),
        refetchType: "none",
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: taskListQueryOptions(
            projectId,
            { ...move.filters, status: [move.from] },
            "board",
          ).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: taskListQueryOptions(
            projectId,
            { ...move.filters, status: [move.to] },
            "board",
          ).queryKey,
        }),
      ]);
    },
  });
}

/**
 * The filter on screen, as a file (§B, and the export criterion).
 *
 * A mutation rather than a query, because it is an act with a side effect on the browser and
 * not a thing to cache: the same filter exported twice is two files, and a cached CSV would be
 * a stale one the moment anybody edits a task.
 *
 * The blob is turned into a download here rather than followed as a URL, because this endpoint
 * answers the bytes directly and the request has to carry the session — the drawings download
 * goes the other way (a signed URL the browser follows) because there the API never sees a
 * byte. The object URL is revoked immediately: the anchor click is synchronous, so by the time
 * this line runs the browser already holds the blob.
 */
export function useExportTasks(projectId: string) {
  return useMutation({
    mutationFn: async (filters: TaskFilters) => {
      const blob = await exportTasksCsv(projectId, filters);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "tasks.csv";
      anchor.click();
      URL.revokeObjectURL(url);
    },
  });
}

/**
 * A hierarchy node id to the name a person put on it.
 *
 * §A took the grouped tree out of this screen; the address a task sits at did not stop
 * mattering, so the register prints it per row instead. It costs no request of its own —
 * `hierarchyQueryOptions` is C1a's key, imported rather than re-declared, so the tree the
 * filter's picker already reads is the tree the rows read (`~/.claude/CLAUDE.md`, data
 * fetching). The map covers drawing slots too: a slot shares its primary key with the node it
 * specialises, and the endpoint returns both kinds.
 *
 * `null` for a task filed nowhere, and for a node the caller's zone scope pruned — the second
 * is not an error and must not print as one. A scoped member reading a task admitted to them
 * from outside their zones sees the task without its address, which is the honest rendering of
 * what the server told them.
 */
export function useNodeNames(
  projectId: string,
): (nodeId: string | null | undefined) => string | null {
  const tree = useQuery(hierarchyQueryOptions(projectId));
  const nodes = tree.data?.nodes;
  return useCallback(
    (nodeId: string | null | undefined): string | null => {
      if (nodeId == null || nodes === undefined) return null;
      return nodes.find((node) => node.id === nodeId)?.name ?? null;
    },
    [nodes],
  );
}

/**
 * The change signal.
 *
 * `staleTime` is left at the client default deliberately: `refetchInterval` is what drives
 * this query, and a stale time above the interval would only mean the first mount after a
 * navigation trusts a value the poll is about to replace anyway.
 *
 * It does not run in a hidden tab. A register left open on a second monitor overnight should
 * not ask 1 200 times before anybody looks at it; it catches up on the next interval after the
 * tab is visible again, and the digest it then reads is the current one either way.
 */
export function tasksVersionQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...tasksKey(projectId), "version"] as const,
    queryFn: () => readTasksVersion(projectId),
    refetchInterval: TASKS_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * The poll, and what a change does — §E's second half, which is the half that costs money.
 *
 * **Call this once, from `TasksPage`.** It is one query key, so a second caller would not
 * double the traffic; it would double the *invalidation*, which is worse and quieter.
 *
 * On a changed digest:
 *
 *   - every list this project has cached is marked stale with `refetchType: 'none'` — the
 *     board's columns while the register is showing, the register's pages while the board is
 *     showing, and every filter combination either of them has held. They are correct again
 *     the moment they are next mounted, at the cost of one request each and only for the ones
 *     somebody actually returns to. The label registry is *not* in that sweep (`listsKey`).
 *   - then the mounted view's queries are invalidated for real, so what the person is looking
 *     at is the thing that asks the server now.
 *
 * The first digest seen is a baseline, not a change: a fresh mount has just fetched its list,
 * and treating the first poll as news would refetch every screen one interval after opening it.
 * **The baseline is per project, which is why the ref holds the id it belongs to.** Moving
 * between two projects' Tasks tabs is the same route, so this hook is not remounted; a bare
 * digest would carry project A's value into project B and read B's first poll as news.
 */
export function useTasksVersionPoll(projectId: string, view: TaskView): void {
  const queryClient = useQueryClient();
  const version =
    useQuery(tasksVersionQueryOptions(projectId)).data?.version ?? null;
  const seen = useRef<{ projectId: string; version: string } | null>(null);

  useEffect(() => {
    if (version === null) return;
    const baseline = seen.current;
    seen.current = { projectId, version };
    if (baseline?.projectId !== projectId || baseline.version === version)
      return;
    void queryClient.invalidateQueries({
      queryKey: listsKey(projectId),
      refetchType: "none",
    });
    void queryClient.invalidateQueries({ queryKey: listKey(projectId, view) });
  }, [projectId, queryClient, version, view]);
}

// --- The task detail (C8) ------------------------------------------------------
//
// Everything below hangs off one task's own prefix, `detailKey`, which is deliberately
// *outside* `listsKey`: the 30 s version poll covers the register and the board and nothing
// here (§E, second implementation note — a comment, an attachment or a watcher writes its own
// table and leaves `task.updated_at` alone, so the poll would answer `304` while the thread
// runs). A detail query goes stale on the client-wide 30 s clock and refetches on the next
// mount or focus like any other screen; its liveness story is its own, and building it on the
// poll would be building on a signal that was documented not to carry it.

/** One task's whole life — the detail, and under it the thread, attachments, watchers, audience. */
function detailKey(projectId: string, taskId: string) {
  return [...tasksKey(projectId), "task", taskId] as const;
}

/**
 * The task, with the validator its `PATCH` demands back.
 *
 * The cache holds the `Representation` pair rather than the bare task, for the reason
 * `features/settings` established on `PATCH /workspace`: the ETag and the body must move
 * together, or the screen edits against a validator that belongs to an older read and earns
 * a `412` the person did nothing to deserve.
 */
export function taskQueryOptions(projectId: string, taskId: string) {
  return queryOptions({
    queryKey: [...detailKey(projectId, taskId)] as const,
    queryFn: () => readTask(taskId),
  });
}

export function useTask(projectId: string, taskId: string) {
  return useQuery(taskQueryOptions(projectId, taskId));
}

export function taskCommentsQueryOptions(projectId: string, taskId: string) {
  return queryOptions({
    queryKey: [...detailKey(projectId, taskId), "comments"] as const,
    queryFn: () => listTaskComments(taskId),
  });
}

export function taskAttachmentsQueryOptions(projectId: string, taskId: string) {
  return queryOptions({
    queryKey: [...detailKey(projectId, taskId), "attachments"] as const,
    queryFn: () => listTaskAttachments(taskId),
  });
}

export function taskWatchersQueryOptions(projectId: string, taskId: string) {
  return queryOptions({
    queryKey: [...detailKey(projectId, taskId), "watchers"] as const,
    queryFn: () => listTaskWatchers(taskId),
  });
}

export function taskAudienceQueryOptions(projectId: string, taskId: string) {
  return queryOptions({
    queryKey: [...detailKey(projectId, taskId), "audience"] as const,
    queryFn: () => readTaskAudience(taskId),
  });
}

/**
 * The drawings this task owns (`KLQ-20260824-001`). Under `detailKey` like the thread and the
 * rails: the version poll deliberately does not cover it — an ownership change writes
 * `drawing_slot`, not `task.updated_at` — so its liveness is the ordinary mount/stale cycle.
 */
export function taskDeliverablesQueryOptions(
  projectId: string,
  taskId: string,
) {
  return queryOptions({
    queryKey: [...detailKey(projectId, taskId), "deliverables"] as const,
    queryFn: () => listTaskDeliverables(taskId),
  });
}

/**
 * A content edit (`PATCH`, with the `If-Match` the cached representation carried).
 *
 * On success the response — body *and* fresh validator — is written straight into the detail
 * entry rather than invalidated: the server returned the full new representation, and an
 * invalidate would leave the cache briefly holding the old ETag, in which window a second
 * save would earn a `412` (the `features/settings` reasoning, verbatim applicable).
 *
 * The lists are marked stale without refetching (rule 5): an edit changes rows the register
 * and the board render, and they are correct again the moment they are next mounted. An
 * assignment can also have admitted a firm, so the audience sublist refetches for real —
 * it is the panel §F says must never quietly lag the act.
 */
export function useUpdateTask(projectId: string, taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ changes, etag }: { changes: TaskUpdate; etag: string }) =>
      updateTask(taskId, changes, etag),
    retry: false,
    onSuccess: async (fresh) => {
      queryClient.setQueryData(
        taskQueryOptions(projectId, taskId).queryKey,
        fresh,
      );
      void queryClient.invalidateQueries({
        queryKey: listsKey(projectId),
        refetchType: "none",
      });
      await queryClient.invalidateQueries({
        queryKey: taskAudienceQueryOptions(projectId, taskId).queryKey,
      });
    },
    onError: (error) => {
      // A `412` means somebody moved the row since this screen read it. Refetching is what
      // makes the sentence on screen true — the person edits what is actually there now,
      // against the fresh validator, instead of re-earning the same refusal.
      if (isStale(error)) {
        void queryClient.invalidateQueries({
          queryKey: taskQueryOptions(projectId, taskId).queryKey,
          exact: true,
        });
      }
    },
  });
}

/**
 * One workflow move, from the detail's buttons (§C, same rules as the board's drop).
 *
 * The response is the moved task but arrives without a validator — `POST /status` sets no
 * `ETag` header — so writing it into the detail entry would pair a new body with an old
 * validator and every later edit would `412`. The entry is invalidated instead (`exact`,
 * so the thread and the rails are not refetched over a status change that wrote none of
 * them), awaited so the buttons the person sees next are the ones the fresh
 * `allowed_transitions` actually offers.
 *
 * `retry: false` for the board's reason: a `409` here is the workflow answering, and the
 * verification gate does not open for persistence.
 */
export function useTaskTransition(projectId: string, taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (to: TaskStatus) => changeTaskStatus(taskId, to),
    retry: false,
    onSuccess: async () => {
      void queryClient.invalidateQueries({
        queryKey: listsKey(projectId),
        refetchType: "none",
      });
      await queryClient.invalidateQueries({
        queryKey: taskQueryOptions(projectId, taskId).queryKey,
        exact: true,
      });
    },
  });
}

/**
 * Reopen: a `201` that creates a task, never an edit of this one.
 *
 * The new row is the mutation's result and the caller renders it — the closed task carries no
 * forward pointer by design (`domains/tasks/service.py:reopen_task`: the old row is not
 * touched), so the freshly created link is the one moment this side can show both ends. The
 * lists go stale without refetching; the new task is in them whenever they are next mounted.
 */
export function useReopenTask(projectId: string, taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => reopenTask(taskId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: listsKey(projectId),
        refetchType: "none",
      });
    },
  });
}

/** A comment posted; only the thread it landed in refetches. */
export function useAddTaskComment(projectId: string, taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, mentionsUserIds }: { body: string; mentionsUserIds: readonly string[] }) =>
      addTaskComment(taskId, body, mentionsUserIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: taskCommentsQueryOptions(projectId, taskId).queryKey,
      });
    },
  });
}

/** What one attach asks for: the picked file, and the idempotency key minted when it was picked. */
export interface TaskAttachmentUpload {
  readonly file: File;
  /**
   * The caller's, not this hook's — minted where the file is picked and kept for as long as
   * that file is the chosen one, so a retry after a failed transfer replays the same staging
   * object instead of minting a second (`features/drawings/upload.ts` states the contract).
   */
  readonly idempotencyKey: string;
  readonly onProgress?: (progress: UploadProgress) => void;
}

/**
 * The whole attach pipeline: hash → `POST /uploads` ticket → PUT the bytes → confirm → poll →
 * `POST /tasks/{id}/attachments` consuming the upload exactly once.
 *
 * The first four steps are `features/drawings/upload.ts`, reused rather than restated — the
 * ticket flow, the `already_present` short-circuit and the scan poll are one pipeline with
 * one implementation. `kind` is derived from the file's own type: a photo is what the site
 * captures, a file is everything else, and `voice_note` waits for the capture surface (E3)
 * that could record one.
 */
export function useAddTaskAttachment(projectId: string, taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      idempotencyKey,
      onProgress,
    }: TaskAttachmentUpload) => {
      const uploaded = await uploadFile(
        file,
        "task_attachment",
        idempotencyKey,
        onProgress,
      );
      const kind = file.type.startsWith("image/") ? "photo" : "file";
      return addTaskAttachment(taskId, uploaded.uploadId, kind);
    },
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: taskAttachmentsQueryOptions(projectId, taskId).queryKey,
      });
    },
  });
}

/**
 * The same pipeline as `useAddTaskAttachment`, but the task id is a mutation variable rather
 * than a hook argument.
 *
 * The create drawer does not know the task's id until the server answers the `POST` that
 * creates it, so the id cannot be a hook parameter — it arrives in the mutation's variables
 * alongside the file. The rest of the pipeline is identical: the ticket flow, the kind
 * derivation and the invalidation all work the same way once the id is in hand.
 */
export function useAttachToNewTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      file,
      idempotencyKey,
      onProgress,
    }: TaskAttachmentUpload & { readonly taskId: string }) => {
      const uploaded = await uploadFile(
        file,
        "task_attachment",
        idempotencyKey,
        onProgress,
      );
      const kind = file.type.startsWith("image/") ? "photo" : "file";
      return addTaskAttachment(taskId, uploaded.uploadId, kind);
    },
    retry: false,
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: taskAttachmentsQueryOptions(projectId, variables.taskId).queryKey,
      });
    },
  });
}

/**
 * Follow, or stop. The `PUT` answers the full new watcher list, so it is written into the
 * entry rather than refetched — a round trip to learn what the response already said.
 */
export function useSetTaskWatching(projectId: string, taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (watching: boolean) => setTaskWatching(taskId, watching),
    onSuccess: (list) => {
      queryClient.setQueryData(
        taskWatchersQueryOptions(projectId, taskId).queryKey,
        list,
      );
    },
  });
}

/**
 * The task's address, as the chain of ancestors the caller may see.
 *
 * Walked off the same cached tree `useNodeNames` reads — no request of its own. The server
 * already decided every visibility question this walk could raise: a caller who may read the
 * task may read its node (`_visible_task` answers `404` otherwise), and the tree they fetch
 * renders the ancestors of their granted zones as structure (`ScopeView.renders`) precisely
 * so a member can orient. So the chain this returns is exactly the chain the server chose to
 * show them — a node the tree does not hold is simply not in it, and rendering the partial
 * chain is the honest rendering of what they were told. The `project` root is skipped: the
 * screen is already inside the project, and its name is the shell's to print.
 */
export function useNodeTrail(
  projectId: string,
): (
  nodeId: string | null | undefined,
) => readonly {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
}[] {
  const tree = useQuery(hierarchyQueryOptions(projectId));
  const nodes = tree.data?.nodes;
  return useCallback(
    (nodeId: string | null | undefined) => {
      if (nodeId == null || nodes === undefined) return [];
      const byId = new Map(nodes.map((node) => [node.id, node]));
      const trail: { id: string; name: string; kind: string }[] = [];
      let current = byId.get(nodeId);
      // Bounded by the map's size: a cycle would be a server defect, and walking it forever
      // here would turn that defect into a frozen tab.
      while (
        current !== undefined &&
        current.kind !== "project" &&
        trail.length < byId.size
      ) {
        trail.unshift({
          id: current.id,
          name: current.name,
          kind: current.kind,
        });
        current =
          current.parent_id == null ? undefined : byId.get(current.parent_id);
      }
      return trail;
    },
    [nodes],
  );
}

/**
 * A person who is no longer on the project, rendered honestly.
 *
 * §G: `TaskResponse` carries ids, names come from the member list, and somebody who has left
 * it has no name to give. An em dash says "we cannot name this person" where a blank would
 * read as "nobody" and a raw UUID would read as a bug. The honest fix is a display name on the
 * payload, which is a backend decision and an unticked criterion rather than a screen detail.
 *
 * **The em dash is reserved for exactly that case**, which is why the resolver below answers a
 * tagged value rather than a string: a member list that has not arrived, and one the server
 * refused, are not the same fact as a person who left, and a screen that printed one dash for
 * all three would be claiming knowledge it does not have.
 */
export const UNKNOWN_PERSON = "—";

/**
 * What the member list can say about one id.
 *
 * `absent` is the null id (nobody is assigned, or the task was never closed) and is the
 * caller's own wording; `departed` is the em dash above, and covers the one thing a *loaded*
 * member list can fail at: it holds no row for this id, so the person has left the project.
 * `pending` and `unavailable` are the two states the register must not paint as `departed`.
 */
export type PersonName =
  | { readonly state: "absent" }
  | { readonly state: "pending" }
  | { readonly state: "unavailable" }
  | { readonly state: "departed" }
  | { readonly state: "named"; readonly name: string };

/**
 * `assignee_user_ids`, `created_by_user_id`, `closed_by_user_id` -> a name, in one helper.
 *
 * One helper and one fetch: `membersQueryOptions` is C10's key, imported rather than
 * re-declared, so opening this tab after the People tab costs nothing and the two screens
 * cannot disagree about who is on the project (`~/.claude/CLAUDE.md`, data fetching).
 *
 * **`unavailable` is the normal state for two of the roles that do most of the task work.**
 * `GET /projects/{id}/members` is gated on `Action.read_people`, whose floor is `architect`
 * (`core/authz.py`), while `Surface.tasks` grants `engineer` and `contractor` write. So those
 * two read tasks they may not read the member list behind, and the call answers `403` — for
 * them *no* id resolves, not just the departed ones. That is a gap in §G rather than a bug
 * here: the fix is a display name on `TaskResponse` (or a narrower endpoint), which is a
 * backend decision. Until it exists this state is what stops the screen from telling an
 * engineer that everyone on their project has left it.
 */
export function useMemberNames(
  projectId: string,
): (userId: string | null | undefined) => PersonName {
  const members = useQuery(membersQueryOptions(projectId));
  const items = members.data?.items;
  const { isPending, isError } = members;
  return useCallback(
    (userId: string | null | undefined): PersonName => {
      if (userId == null) return { state: "absent" };
      if (isError) return { state: "unavailable" };
      if (isPending || items === undefined) return { state: "pending" };
      // Two different facts, kept apart. **Absence from the list is the only thing that means
      // departed** — `display_name` and `email` are both nullable and the backend nulls *both*
      // together whenever its `_join()` gets no person row (`projects/service.py`), so reading
      // a missing name as a departure would put the em dash on somebody who is demonstrably
      // still on the project. A row that is present and nameless is a name we cannot read.
      const member = items.find((row) => row.user_id === userId);
      if (member === undefined) return { state: "departed" };
      const name = member.display_name ?? member.email ?? null;
      return name === null
        ? { state: "unavailable" }
        : { state: "named", name };
    },
    [isError, isPending, items],
  );
}

/**
 * The other half of the helper: one `PersonName` -> one string, in one place.
 *
 * The resolver above decides *what is known*; this decides *what is printed*, and the two are
 * split because only the second needs a locale. Keeping them together in this module is the
 * point — the em dash means "this person left the project" and nothing else, and a rule that
 * every call site re-derives is a rule that holds until the first call site forgets it
 * (retro rule 8: a cross-cutting filter lands as one reusable object the first time it
 * appears).
 *
 * `absent` is the caller's own word because there is no shared one: an unassigned task says
 * *Unassigned*, a task that was never closed says nothing at all, and a filter chip says
 * *Anyone*. That is the one state this function refuses to name for you.
 */
export function renderPersonName(
  t: Translate<"tasks">,
  name: PersonName,
  absent: string,
): string {
  switch (name.state) {
    case "absent":
      return absent;
    case "pending":
      return t("person.pending");
    case "unavailable":
      return t("person.unavailable");
    case "departed":
      return UNKNOWN_PERSON;
    case "named":
      return name.name;
  }
}

/**
 * The assignee *set* (`KLQ-20260824-001`), printed as one line. The empty set is the caller's
 * own word for the same reason `absent` is above — a row says *Unassigned*, a filter chip says
 * *Anyone*. Everybody named resolves through `renderPersonName`, so the em-dash rule and the
 * pending/unavailable split have exactly one printer for a list as for a scalar.
 */
export function renderPersonNames(
  t: Translate<"tasks">,
  nameOf: (userId: string | null | undefined) => PersonName,
  userIds: readonly string[],
  absent: string,
): string {
  if (userIds.length === 0) return absent;
  return userIds
    .map((userId) => renderPersonName(t, nameOf(userId), absent))
    .join(", ");
}

/* --- A drawing's tasks (`MCK-20260521-008`, the coordination view) ---------------------- */

/**
 * What one drawing still owes — the publish flow's proposal and the slot detail's open half.
 *
 * **Under `listsKey`**, so every sweep that marks the project's task lists stale marks this one
 * too: a task closed on the board is a task no longer owed by its drawing, and a key outside the
 * sweep would keep offering it. One request per drawing opened; nothing polls it.
 */
export function slotTasksQueryOptions(projectId: string, slotId: string) {
  return queryOptions({
    queryKey: [...listsKey(projectId), "slot", slotId] as const,
    queryFn: () => listSlotTasks(projectId, slotId),
  });
}

export function useSlotTasks(projectId: string, slotId: string) {
  return useQuery(slotTasksQueryOptions(projectId, slotId));
}

/** One task the publish flow could not close, and the server's reason. */
export interface ResolveFailure {
  readonly task: Task;
  readonly error: unknown;
}

/**
 * Close the ticked tasks against the version just published.
 *
 * **One task at a time, and each walked to `done` first when it is not there yet.** `closed` is
 * reachable only from `done` (`domains/tasks/service.py:_TRANSITIONS`), and publishing the sheet
 * is the work being done — so a ticked `open` task is moved `done` → `closed` rather than
 * refused. Each step is the server's ordinary transition with its ordinary authority and
 * verification gate; nothing here decides who may close what.
 *
 * **A task that stops half-way says so.** When the verification gate refuses the close, the
 * task is left `done` — true, since the work is published — and returned as a failure with the
 * server's own sentence naming who has to close it. Sequential rather than parallel because
 * the list is a handful and the failures are reported in the order they were ticked.
 */
export function useCloseTasksByVersion(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      versionId,
      tasks,
    }: {
      readonly versionId: string;
      readonly tasks: readonly Task[];
    }): Promise<readonly ResolveFailure[]> => {
      const failures: ResolveFailure[] = [];
      for (const task of tasks) {
        try {
          if (task.status !== "done") await changeTaskStatus(task.id, "done");
          await closeTaskByVersion(task.id, versionId);
        } catch (error) {
          failures.push({ task, error });
        }
      }
      return failures;
    },
    retry: false,
    onSettled: async (_data, _error, variables) => {
      for (const task of variables.tasks) {
        void queryClient.invalidateQueries({
          queryKey: taskQueryOptions(projectId, task.id).queryKey,
          exact: true,
        });
      }
      await queryClient.invalidateQueries({ queryKey: listsKey(projectId) });
    },
  });
}
